import {combineReducers} from 'redux';
import userReducer from './user';
import configReducer from './config';
import showReducer from './show';
import episodeReducer from './episode';
import movieReducer from './movie';
import favouriteReducer from './favourite';
import recommendationReducer from './recommendation';
import drmReducer from './drm';
import videoReducer from './video';
import toast from './toast';
import modal from './modal';
import kidsActivities from './kidsActivities';
import comicsReducer from './comics';
import livesReducer from './lives';
import {createSelector} from 'reselect';
import homeReducer from './home';
import pnpReducer from './pnp';
import gamesReducer from './games';
import stickersReducer from './stickers';
import playerReducer from './player';
import sponsorReducer from './sponsor';

export default combineReducers({
    config: configReducer,
    user: userReducer,
    show: showReducer,
    episode: episodeReducer,
    movie: movieReducer,
    favourite: favouriteReducer,
    recommendation: recommendationReducer,
    drm: drmReducer,
    video: videoReducer,
    ui: combineReducers({
        toast,
        modal,
    }),
    kidsActivities: kidsActivities,
    comics: comicsReducer,
    lives: livesReducer,
    home: homeReducer,
    pnp: pnpReducer,
    games: gamesReducer,
    stickers: stickersReducer,
    player: playerReducer,
    sponsor: sponsorReducer,
});

export const getHomeData = state => state.home.rows;
export const getHomeError = state => state.home.error;

export const selectKidsActivities = state => state.kidsActivities.activities;
export const selectKidsActivitiesError = state => state.kidsActivities.error;

export const selectCanAutoPlayVideo = state => !!state.video.autoplay;

export const selectRecentPlayedGames = (state, games) => {
    //map game ids into games, removing non existing games
    return (state.user.recentlyPlayedGames || [])
        .map(gameId => {
            return games.find(game => game.id === gameId);
        })
        .filter(game => !!game);
};

export const selectComics = state => state.comics.comics;
export const selectComicsError = state => state.comics.error;

export const selectRecentChapters = createSelector([selectComics], comics => {
    if (!comics) {
        return [];
    }
    let allChapters = comics?.reduce((prev, cur) => {
        return prev.concat(cur.chapters);
    }, []);
    let sorted = allChapters?.sort((chapter1, chapter2) =>
        chapter1.releaseDate > chapter2.releaseDate ? -1 : 1,
    );

    return sorted?.slice(0, 3);
});

export const selectComicById = (state, id) => {
    return state.comics.comics?.find(comic => comic.id === id);
};

export const selectComicByFriendlyName = (state, name) => {
    return state.comics.comics?.find(comic => comic.friendlyName === name);
};

export const selectComicsForChapter = (state, chapterId) => {
    return state.comics.comics?.find(
        comic =>
            comic.chapters.findIndex(chapter => chapter.id === chapterId) !==
            -1,
    );
};

export const selectProgressForChapters = (state, chapterIds) => {
    return chapterIds.map(chapterId => {
        return selectProgressForChapter(state, chapterId);
    });
};

export const selectProgressForChapter = (state, chapterId) => {
    return state.user.chapterProgress?.[chapterId]?.progress || 0;
};

export const selectDatedProgressForChapter = (state, chapterId) => {
    return state.user.chapterProgress?.[chapterId];
};

export const selectChapterRead = (state, chapterId) => {
    if (!chapterId) {
        return undefined;
    }
    return state.user.readChapters?.includes(chapterId);
};

export const selectCurrentChapter = (state, comic) => {
    if (!comic) {
        return undefined;
    }
    //get progress for all chapters
    let progress = selectProgressForChapters(
        state,
        comic.chapters.map(c => c.id),
    );
    //search the last chapter that has progress, but it is lower than 1
    let index = findLastIndex(progress, (p, i) => {
        return p > 0 || selectChapterRead(state, comic.chapters[i].id);
    });

    //select next one if this one is read
    if (index >= 0 && selectChapterRead(state, comic.chapters[index]?.id)) {
        index = Math.min(index + 1, progress.length - 1);
    }
    return comic.chapters[index];
};

export const selectPendingChapters = state => {
    //based on current comics and chapter progress, list next chapters to read
    let comics = selectComics(state);
    //for each comic, get unread chapters (ignore the fully read ones)
    let unreadChapters = comics
        ?.map(comic => {
            return comic.chapters.reduce((prev, cur) => {
                let chapterProgress = selectDatedProgressForChapter(
                    state,
                    cur.id,
                );
                if (
                    chapterProgress &&
                    chapterProgress.progress > 0 &&
                    (state.user.readChapters || []).indexOf(cur.id) === -1
                ) {
                    prev.push({chapter: cur, date: chapterProgress.date});
                }
                return prev;
            }, []);
        })
        .filter(chapters => chapters.length > 0);

    let unreadList =
        unreadChapters?.reduce((prev, cur) => {
            return prev.concat(cur);
        }, []) || [];

    //sort by last first
    unreadList = unreadList
        .sort((progress1, progress2) => {
            return progress1.date > progress2.date ? -1 : 1;
        })
        .map(chapter => {
            //remove date and return just the chapter
            return chapter.chapter;
        });

    return unreadList;
};

export const selectShouldShowComicsTutorial = state => {
    return !state.user.comicsTutorialShown;
};

export const selectLives = state => state.lives.lives;

export const selectPushEnabled = state => state.user.pushEnabled === true;

export const selectIsBadgeHidden = state => state.user.hideBadge === true;

export const selectPnP = state => state.pnp.pnp;

export const selectGames = state => state.games.games;

export const selectGamesError = state => state.games.error;

export const selectDisplayGameFullScreen = state =>
    !!state.games.nextInFullScreen;

function findLastIndex(array, predicate) {
    let l = array.length;
    while (l--) {
        if (predicate(array[l], l, array)) return l;
    }
    return -1;
}

export const selectUserLocation = state => state.user.countryIsoCode || 'US';

export const selectDKidsEnabled = state =>
    selectUserLocation(state) !== 'AR' && selectUserLocation(state) !== 'US';

export const selectHolidayTheme = state => {
    return '';
    //return state.config.theme; //config has cache?
};

export const selectAgeRating = state => state.user.ageRating || 12;

export const selectStickerPacks = state => state.stickers.packs;

export const selectShows = state => state.show.shows;
